import React, { useState } from 'react';
import Dropdown from 'react-dropdown';
import classNames from 'classnames';
import get from 'lodash.get';
import { GENRE_TYPES, MEASURE_NAMES } from '../../constants/assessments';
import Button from '../buttons/Button';
import RadioButton from '../buttons/RadioButton';
import { useAppContext } from '../contexts/AppContext';
import { usePopupContext } from '../contexts/PopupContext';
import { constructBurstAssessmentCSLink, linkOnClickHandler } from '../navigation/navigation';
import MeasureSelectionDropdownItem from './MeasureSelectionDropdownItem';
import CloseButton from '../buttons/CloseButton';
import { GRADE_LABEL, PERIOD } from '../../constants/enrollment';
import ScreenOverlay from '../../components/body/ScreenOverlay';
import { timesFormBurstWasUsedInResults } from '../utils/popupDataHelper';
import MeasureSelectionDropdownLegendItem from './MeasureSelectionDropdownLegendItem';
import { getStudentResults, getAllStudentResults } from '../utils/studentsListHelpers';
import { BURST } from '../../constants/applications';

const getProbeConfigName = probeConfig => {
    return probeConfig
        ? `${GRADE_LABEL[probeConfig.grade]} ${
              probeConfig.is_alt === true ? 'ALT' : probeConfig.period
          } ${probeConfig.genre}`
        : ' ';
};

const getFormSelectionContent = (
    isAlt,
    probeConfig,
    formOptions,
    setProbeConfigByGenre,
    setProbeConfigById,
) =>
    isAlt ? (
        <Dropdown
            controlClassName='dashboard-form-selection-dropdown'
            arrowClassName='dashboard-form-selection-dropdown-arrow'
            menuClassName='dashboard-form-selection-dropdown-menu'
            options={formOptions}
            value={formOptions.find(form => form.value === probeConfig.id)}
            onChange={e => setProbeConfigById(e.value)}
        />
    ) : (
        <>
            <RadioButton
                id={GENRE_TYPES.FICTION}
                key={GENRE_TYPES.FICTION}
                value={GENRE_TYPES.FICTION}
                name="Fiction"
                isChecked={probeConfig.genre === GENRE_TYPES.FICTION}
                onChange={e => setProbeConfigByGenre(e.currentTarget.value)}
            />
            <RadioButton
                id={GENRE_TYPES.NONFICTION}
                key={GENRE_TYPES.NONFICTION}
                value={GENRE_TYPES.NONFICTION}
                name="Nonfiction"
                isChecked={probeConfig.genre === GENRE_TYPES.NONFICTION}
                onChange={e => setProbeConfigByGenre(e.currentTarget.value)}
            />
        </>
    );

const getFormPopupHeader = (isAlt, probeConfig) =>
    isAlt ? (
        <span>
            This CS probe was assessed last time using the {getProbeConfigName(probeConfig)} form.
            You may reuse that form or use an alternate form.
        </span>
    ) : (
        <span>Select a fiction or nonfiction Benchmark form.</span>
    );

const CSFormSelectionPopup = () => {
    const { bmWindow, formsMap, classId, allResults } = useAppContext();
    const {
        params: { studentId, isAlt, formData, lastAssessedProbeId },
        actions: { hideCSFormSelectionPopup },
    } = usePopupContext();
    const measure = MEASURE_NAMES.CS_PASSAGE;
    const updatedPeriod = bmWindow.period === PERIOD.SUMMER ? PERIOD.EOY : bmWindow.period;
    const params = {
        studentId,
        classeId: classId,
        period: updatedPeriod,
        measure,
    };
    const defaultProbeConfig = formData.find(
        config =>
            config.is_alt === false &&
            config.measure === measure &&
            config.genre === GENRE_TYPES.FICTION,
    );

    const [probeConfigSource, setProbeConfigSource] = useState(defaultProbeConfig);
    const lastProbeConfig = formsMap[lastAssessedProbeId];

    const studentResults = getStudentResults(allResults, studentId, getAllStudentResults);

    const timesUsedInResults = form =>
        timesFormBurstWasUsedInResults(form.probeId, get(studentResults, BURST));

    const formOptions = [
        {
            label: <MeasureSelectionDropdownLegendItem />,
            className: classNames('dashboard-option-disabled'),
        },
        ...formData.map(form => ({
            value: form.id,
            label: (
                <MeasureSelectionDropdownItem
                    label={getProbeConfigName(form)}
                    numOfUsedForms={timesUsedInResults(form)}
                />
            ),
        })),
    ];

    const setProbeConfigByGenre = genre => {
        const probeConfig = formData.find(
            config =>
                config.is_alt === false && config.measure === measure && config.genre === genre,
        );
        setProbeConfigSource(probeConfig);
    };
    const setProbeConfigById = formId => {
        const probeConfig = formData.find(config => config.id === formId);
        setProbeConfigSource(probeConfig);
    };

    const onSubmitButtonClick = e => {
        const assessmentLink = constructBurstAssessmentCSLink({
            ...params,
            is_alt: probeConfigSource.is_alt ? 1 : 0,
            probeId: probeConfigSource.probeId,
            genre: probeConfigSource.genre,
            seq: probeConfigSource.seq,
        });
        hideCSFormSelectionPopup();
        linkOnClickHandler(assessmentLink)(e);
    };
    return (
        <ScreenOverlay>
            <div
                className={classNames('dashboard-form-selection-popup-wrapper', {
                    'dashboard-alternative-form-popup': isAlt,
                })}
            >
                <div className='dashboard-form-selection-popup-header'>
                    <p>{isAlt ? 'Alternate' : ' '} Form Selection</p>
                    <CloseButton onClick={hideCSFormSelectionPopup} />
                </div>
                <div className='dashboard-form-selection-popup-body'>
                    {getFormPopupHeader(isAlt, lastProbeConfig)}
                    {getFormSelectionContent(
                        isAlt,
                        probeConfigSource,
                        formOptions,
                        setProbeConfigByGenre,
                        setProbeConfigById,
                    )}
                    <div className='dashboard-controls'>
                        <Button onClick={onSubmitButtonClick} />
                    </div>
                </div>
            </div>
        </ScreenOverlay>
    );
};

export default CSFormSelectionPopup;
